/**
 * localStorage key written by SocketProvider on user activity (shared with midnight scheduler).
 */
export const LAST_ACTIVITY_KEY = 'lastActivityTimestamp';

/**
 * Experimental flag key (matches `/experimental` Flags page: `flag:useTestActivityTimers`).
 * Read directly from localStorage to avoid circular imports with routes/Flags.
 */
const TEST_ACTIVITY_TIMERS_FLAG_KEY = 'flag:useTestActivityTimers';

const ACTIVITY_TIMER_MODE: 'production' | 'testing' =
    typeof localStorage !== 'undefined' && localStorage.getItem(TEST_ACTIVITY_TIMERS_FLAG_KEY) === 'YES' ? 'testing' : 'production';

// --- Timer values for each mode (milliseconds/seconds as noted) ---
const ACTIVITY_TIMER_VALUES = {
    production: {
        PAGE_IDLE_TIMEOUT_MS: 2 * 60 * 60 * 1000, // 2 hours
        MIDNIGHT_INACTIVITY_MS: 60 * 60 * 1000, // 1 hour
        MIDNIGHT_SCHEDULE_MS: null as number | null, // real Europe/Berlin midnight
        AWAY_EXTENSION_TRIGGER_MS: 20 * 60 * 1000, // 20 minutes
        AWAY_COUNTDOWN_SECONDS: 60 * 60, // 60 minutes
        IDLE_NOTIFICATION_AUTO_CLOSE_MS: 30 * 60 * 1000, // 30 minutes
        LOBBY_INACTIVITY_TIMEOUT_MS: 10 * 60 * 60 * 1000, // 10 hours
        LOBBY_INACTIVITY_WARNING_SECONDS: 5 * 60, // 5 minutes
        OOO_MANUAL_INACTIVITY_MS: 4 * 60 * 60 * 1000, // 4 hours
        IDLE_AWAY_TO_OOO_MS: 30 * 60 * 1000, // 30 minutes (idle chain: away → out-of-office)
        IDLE_OOO_TO_PROMPT_MS: 4 * 60 * 60 * 1000, // 4 hours (idle chain: out-of-office → presence prompt)
        PRESENCE_PROMPT_SECONDS: 5 * 60 // 5 minutes ("Are you still there?" countdown)
    },
    testing: {
        PAGE_IDLE_TIMEOUT_MS: 20 * 1000, // 20 seconds
        MIDNIGHT_INACTIVITY_MS: 1 * 60 * 1000, // 1 minute
        MIDNIGHT_SCHEDULE_MS: 2 * 60 * 1000, // fire midnight rule in 2 minutes
        AWAY_EXTENSION_TRIGGER_MS: 20 * 1000, // 20 seconds
        AWAY_COUNTDOWN_SECONDS: 60, // 60 seconds
        IDLE_NOTIFICATION_AUTO_CLOSE_MS: 30 * 1000, // 30 seconds
        LOBBY_INACTIVITY_TIMEOUT_MS: 2 * 60 * 1000, // 2 minutes
        LOBBY_INACTIVITY_WARNING_SECONDS: 60, // 60 seconds
        OOO_MANUAL_INACTIVITY_MS: 40 * 1000, // 40 seconds
        IDLE_AWAY_TO_OOO_MS: 20 * 1000, // 20 seconds
        IDLE_OOO_TO_PROMPT_MS: 40 * 1000, // 40 seconds
        PRESENCE_PROMPT_SECONDS: 30 // 30 seconds
    }
} as const;

// --- Exported timer constants; mode from /experimental "Use test activity timers" ---
export const PAGE_IDLE_TIMEOUT_MS = ACTIVITY_TIMER_VALUES[ACTIVITY_TIMER_MODE].PAGE_IDLE_TIMEOUT_MS;
export const MIDNIGHT_INACTIVITY_MS = ACTIVITY_TIMER_VALUES[ACTIVITY_TIMER_MODE].MIDNIGHT_INACTIVITY_MS;
export const MIDNIGHT_SCHEDULE_MS = ACTIVITY_TIMER_VALUES[ACTIVITY_TIMER_MODE].MIDNIGHT_SCHEDULE_MS;
export const AWAY_EXTENSION_TRIGGER_MS = ACTIVITY_TIMER_VALUES[ACTIVITY_TIMER_MODE].AWAY_EXTENSION_TRIGGER_MS;
export const AWAY_COUNTDOWN_SECONDS = ACTIVITY_TIMER_VALUES[ACTIVITY_TIMER_MODE].AWAY_COUNTDOWN_SECONDS;
export const IDLE_NOTIFICATION_AUTO_CLOSE_MS = ACTIVITY_TIMER_VALUES[ACTIVITY_TIMER_MODE].IDLE_NOTIFICATION_AUTO_CLOSE_MS;
export const LOBBY_INACTIVITY_TIMEOUT_MS = ACTIVITY_TIMER_VALUES[ACTIVITY_TIMER_MODE].LOBBY_INACTIVITY_TIMEOUT_MS;
export const LOBBY_INACTIVITY_WARNING_SECONDS = ACTIVITY_TIMER_VALUES[ACTIVITY_TIMER_MODE].LOBBY_INACTIVITY_WARNING_SECONDS;
export const OOO_MANUAL_INACTIVITY_MS = ACTIVITY_TIMER_VALUES[ACTIVITY_TIMER_MODE].OOO_MANUAL_INACTIVITY_MS;
export const IDLE_AWAY_TO_OOO_MS = ACTIVITY_TIMER_VALUES[ACTIVITY_TIMER_MODE].IDLE_AWAY_TO_OOO_MS;
export const IDLE_OOO_TO_PROMPT_MS = ACTIVITY_TIMER_VALUES[ACTIVITY_TIMER_MODE].IDLE_OOO_TO_PROMPT_MS;
export const PRESENCE_PROMPT_SECONDS = ACTIVITY_TIMER_VALUES[ACTIVITY_TIMER_MODE].PRESENCE_PROMPT_SECONDS;

/**
 * === Quick reference for maintainers ===
 * Toggle "Use test activity timers" on /experimental (reloads the page).
 * Switches all inactivity/away/lobby/midnight timers to short testing values.
 */
